/**
 * WB4-CP1 — Command palette operating layer contract (6-group taxonomy).
 */

import type { WorkbenchAction } from './action-types';
import type { CommandEntry } from './command-registry';
import type { ResolvedCommand } from './command-resolver';

export type CommandPaletteGroup = 'go' | 'act' | 'filter' | 'flow' | 'create' | 'explain';

export const COMMAND_PALETTE_GROUP_ORDER = [
  'go',
  'act',
  'filter',
  'flow',
  'create',
  'explain',
] as const satisfies readonly CommandPaletteGroup[];

export const COMMAND_PALETTE_GROUP_LABELS: Record<CommandPaletteGroup, string> = {
  go: '이동',
  act: '작업',
  filter: '필터',
  flow: 'Flow',
  create: '생성',
  explain: '설명',
};

/** Empty query + object context — Act group shows top N kernel actions. */
export const PALETTE_PINNED_TOP_ACTION_LIMIT = 5;

export const WORKBENCH_EXPLAIN_HANDLER_PREFIX = 'workbench:explain:' as const;

export type PaletteGroupedCommands = Record<CommandPaletteGroup, readonly ResolvedCommand[]>;

export function createEmptyPaletteGroupedCommands(): PaletteGroupedCommands {
  return {
    go: [],
    act: [],
    filter: [],
    flow: [],
    create: [],
    explain: [],
  };
}

function resolveHandlerRef(
  command: Pick<ResolvedCommand, 'handlerRef' | 'actionRef'>
): string | undefined {
  return command.handlerRef ?? command.actionRef;
}

export function isExplainPaletteHandlerRef(handlerRef: string | undefined): boolean {
  return handlerRef?.startsWith(WORKBENCH_EXPLAIN_HANDLER_PREFIX) ?? false;
}

export function buildExplainPaletteHandlerRef(sourceActionId: string): string {
  return `${WORKBENCH_EXPLAIN_HANDLER_PREFIX}${sourceActionId}`;
}

export function resolveRegistryCommandPaletteGroup(entry: CommandEntry): CommandPaletteGroup {
  if (entry.paletteGroup) {
    return entry.paletteGroup;
  }
  if (entry.kind === 'explain') {
    return 'explain';
  }
  if (entry.kind === 'navigate' || entry.kind === 'search') {
    return 'go';
  }
  if (entry.actionRef?.startsWith('flow:start:')) {
    return 'flow';
  }
  if (entry.id.startsWith('filter-') || entry.id.startsWith('cmd-filter-')) {
    return 'filter';
  }
  if (entry.id.startsWith('create-') || entry.id.startsWith('cmd-create-')) {
    return 'create';
  }
  return 'act';
}

export function resolveKernelActionPaletteGroup(
  action: Pick<WorkbenchAction, 'handlerRef' | 'group'>
): CommandPaletteGroup {
  if (action.handlerRef.startsWith('flow:start:')) {
    return 'flow';
  }
  if (action.group === 'create') {
    return 'create';
  }
  return 'act';
}

export function resolveResolvedCommandPaletteGroup(
  command: Pick<ResolvedCommand, 'kind' | 'handlerRef' | 'actionRef' | 'id'> & {
    group?: CommandPaletteGroup;
  }
): CommandPaletteGroup {
  if (command.group) {
    return command.group;
  }
  const handlerRef = resolveHandlerRef(command);
  if (isExplainPaletteHandlerRef(handlerRef)) {
    return 'explain';
  }
  if (command.kind === 'navigate' || command.kind === 'search') {
    return 'go';
  }
  if (handlerRef?.startsWith('flow:start:')) {
    return 'flow';
  }
  if (command.id.startsWith('filter-') || command.id.startsWith('cmd-filter-')) {
    return 'filter';
  }
  if (command.id.startsWith('create-') || command.id.startsWith('cmd-create-')) {
    return 'create';
  }
  return 'act';
}

export function buildExplainPaletteCommands(source: readonly ResolvedCommand[]): ResolvedCommand[] {
  const explainEntries: ResolvedCommand[] = [];

  for (const command of source) {
    if (command.group === 'explain' || !command.disabled || !command.reason?.trim()) {
      continue;
    }
    if (command.group === 'go') {
      continue;
    }
    explainEntries.push({
      id: `explain:${command.id}`,
      label: `${command.label} — ${command.reason.trim()}`,
      section: 'action',
      kind: 'action',
      group: 'explain',
      handlerRef: buildExplainPaletteHandlerRef(command.id),
      priority: command.priority,
      reason: command.reason.trim(),
    });
  }

  return explainEntries;
}

export function applyPaletteTopActionPinPolicy(
  commands: readonly ResolvedCommand[],
  input: { query: string; hasObjectContext: boolean }
): ResolvedCommand[] {
  const normalizedQuery = input.query.trim();
  if (normalizedQuery || !input.hasObjectContext) {
    return [...commands];
  }

  const enabledAct = commands.filter((command) => command.group === 'act' && !command.disabled);
  const pinnedIds = new Set(
    enabledAct
      .slice()
      .sort((a, b) => b.priority - a.priority)
      .slice(0, PALETTE_PINNED_TOP_ACTION_LIMIT)
      .map((command) => command.id)
  );

  return commands.filter((command) => {
    if (command.group !== 'act' || command.disabled) {
      return true;
    }
    return pinnedIds.has(command.id);
  });
}

export function partitionCommandsByPaletteGroup(
  commands: readonly ResolvedCommand[]
): PaletteGroupedCommands {
  const grouped = createEmptyPaletteGroupedCommands();
  const buckets: Record<CommandPaletteGroup, ResolvedCommand[]> = {
    go: [...grouped.go],
    act: [...grouped.act],
    filter: [...grouped.filter],
    flow: [...grouped.flow],
    create: [...grouped.create],
    explain: [...grouped.explain],
  };

  for (const command of commands) {
    const group = resolveResolvedCommandPaletteGroup(command);
    buckets[group].push({ ...command, group });
  }

  for (const group of COMMAND_PALETTE_GROUP_ORDER) {
    buckets[group].sort((a, b) => b.priority - a.priority);
  }

  return buckets;
}

export function flattenPaletteGroupedCommands(
  grouped: PaletteGroupedCommands
): readonly ResolvedCommand[] {
  return COMMAND_PALETTE_GROUP_ORDER.flatMap((group) => grouped[group]);
}
